import { Dispatch, SetStateAction, useMemo, useState } from 'react';

export interface StableActions<T> {
  add: (item: T) => void;
  addMultiple: (items: T[]) => void;
  remove: (item: T) => void;
  removeMultiple: (items: T[]) => void;
  toggle: (item: T) => void;
  removeAll: (filter?: (item: T) => boolean) => void;
  reset: () => void;
  addSingle: (item: T) => void;
  setList: Dispatch<SetStateAction<T[]>>;
}

const useListActions = <T,>({
  selectedItems = [],
  keyExtractor,
  updateOnAction,
  onChange,
}: {
  selectedItems: T[];
  keyExtractor: (val: T) => string;
  updateOnAction?: boolean;
  onChange?: (val: T[]) => void;
}): [T[], StableActions<T>] => {
  const [list, setList] = useState<T[]>(selectedItems);

  const stableActions = useMemo(() => {
    const add = (item: T) => {
      setList((prevList) => {
        const itemExist = prevList.find(
          (listItem) => keyExtractor(listItem) === keyExtractor(item),
        );
        if (itemExist) {
          return prevList;
        }
        const newList = [...prevList, item];
        updateOnAction && onChange?.(newList);
        return newList;
      });
    };

    const addMultiple = (items: T[]) => {
      setList((prevList) => {
        const itemsUniqueKeys = items.map(keyExtractor);
        const filteredList = prevList.filter(
          (listItem) => !itemsUniqueKeys.includes(keyExtractor(listItem)),
        );
        const newList = [...filteredList, ...items];
        updateOnAction && onChange?.(newList);
        return newList;
      });
    };

    const remove = (item: T) => {
      setList((prevList) => {
        const newList = prevList.filter(
          (listItem) => keyExtractor(listItem) !== keyExtractor(item),
        );
        updateOnAction && onChange?.(newList);
        return newList;
      });
    };

    const removeMultiple = (items: T[]) => {
      setList((prevList) => {
        const itemsUniqueKeys = items.map(keyExtractor);
        const newList = prevList.filter(
          (listItem) => !itemsUniqueKeys.includes(keyExtractor(listItem)),
        );
        updateOnAction && onChange?.(newList);
        return newList;
      });
    };

    const removeAll = (filter?: (item: T) => boolean) => {
      if (filter) {
        setList((prevList) => {
          const newList = prevList.filter(filter);
          updateOnAction && onChange?.(newList);
          return newList;
        });
      } else {
        updateOnAction && onChange?.([]);
        setList([]);
      }
    };

    const toggle = (item: T) => {
      setList((prevList) => {
        const itemExist = prevList.find(
          (listItem) => keyExtractor(listItem) === keyExtractor(item),
        );
        if (itemExist !== undefined) {
          const newList = prevList.filter(
            (listItem) => keyExtractor(listItem) !== keyExtractor(item),
          );
          updateOnAction && onChange?.(newList);
          return newList;
        }
        const newList = [...prevList, item];
        updateOnAction && onChange?.(newList);
        return newList;
      });
    };

    const addSingle = (item: T) => {
      setList([item]);
      updateOnAction && onChange?.([item]);
    };

    return { add, addMultiple, remove, removeMultiple, removeAll, toggle, addSingle };
  }, [keyExtractor, onChange, updateOnAction]);

  const reset = () => {
    updateOnAction && onChange?.(selectedItems);
    setList(selectedItems);
  };

  const utils = {
    ...stableActions,
    reset,
    setList,
  } as StableActions<T>;

  return [list, utils];
};

export default useListActions;
